import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Row, Col, Card, Typography, Space, Button, Divider, List, Collapse, theme, Spin, Alert, InputNumber, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { uniq } from 'lodash';
import htmlToPlainText from '@/components/HtmlToPlainText';

import { bookingClear, bookingAvailabilityRequest, upsertQty, setSearch } from '@/AppRedux/actions/booking';
import { Rate, RoomData } from '@/AppRedux/actions/booking/bookingApiType';
import { CartItem } from '@/AppRedux/actions/booking/bookingType';
import NoRoomsAvailable from '@/modules/booking/components/NoRoomsAvailable';
import BookingCutoffModal from '@/modules/booking/components/Modals/BookingCutoffModal';
import BookingStateHook from '@/hooks/useBookingState';
import useIsMobile from '@/hooks/useIsMobile';
import RoomCard from '@/modules/booking/components/RoomCard';
import BookingSearchCardVersion2 from '@/modules/booking/components/BookingSearchCard';
import { getCurrentLanguage } from '@/modules/i18n/i18n.utils';
import { useAppSelector } from '@/AppRedux/store/hooks';
import { isCheckInDateBlocked } from '@/utils/bookingCutoff';
import { DEFAULT_CURRENCY } from '@/constants/AppConfig';
import GoogleHotelMarkup from '@/components/google-hotel/GoogleHotelMarkup';
import { GoogleHotelProfile, GoogleHotelSearch } from '@/components/google-hotel/types';

const Availability: React.FC = () => {
  const { token } = theme.useToken();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { search, availableRooms, cart, loading, error } = BookingStateHook();
  const hotelSettings = useAppSelector((state) => state.hotelProfile.hotelSettings);
  const hotelProfile = useAppSelector((state) => state.hotelProfile.dataSource);
  const intl = useIntl();
  const isMobile = useIsMobile(992);
  const [cutoffBlocked, setCutoffBlocked] = useState(false);

  const current = getCurrentLanguage();

  const { propcode } = useParams<{ propcode: string }>();
  const [searchParams] = useSearchParams();

  const startDate = searchParams.get('start_date');
  const endDate = searchParams.get('end_date');
  const adults = searchParams.get('adults');
  const children = searchParams.get('children');
  const infants = searchParams.get('infants');

  const buildCartKey = useCallback(
    (room: RoomData, rate: Rate) => `${room.roomtype_id}-${rate.rate_id}`,
    [],
  );

  // qtyMap: qty ต่อ rate (key = cartKey) / cartTotalsByRoomType: ผลรวม qty ต่อ room type (แชร์ quota ข้าม rate)
  const { qtyMap, cartTotalsByRoomType } = useMemo(() => {
    const qtyMap: Record<string, number> = {};
    const cartTotalsByRoomType: Record<string, number> = {};
    cart.forEach(item => {
      const key = item.cartKey || buildCartKey(item.room, item.rate);
      qtyMap[key] = item.qty;
      cartTotalsByRoomType[item.room.roomtype_id] = (cartTotalsByRoomType[item.room.roomtype_id] || 0) + item.qty;
    });
    return { qtyMap, cartTotalsByRoomType };
  }, [cart, buildCartKey]);

  useEffect(() => {
    if (!startDate || !endDate) {
      navigate('/booking');
      return;
    }
    const parsedAdults = Number(adults) || 2;
    const parsedChildren = Number(children) || 0;
    const parsedInfants = Number(infants) || 0;
    // Sync URL params → Redux so BookingSearchCard displays values matching the URL
    dispatch(setSearch({
      checkIn: startDate,
      checkOut: endDate,
      adults: parsedAdults,
      children: parsedChildren,
      infants: parsedInfants,
      ...(adults !== null && { guestTouched: true }),
    }));
    dispatch(bookingClear());
    const blocked = isCheckInDateBlocked(dayjs(startDate), hotelSettings);
    setCutoffBlocked(blocked);
    const dateDiff = dayjs(endDate).diff(dayjs(startDate), 'day');
    if (!blocked && dateDiff > 0) {
      dispatch(bookingAvailabilityRequest({ checkIn: startDate, checkOut: endDate, adults: parsedAdults, children: parsedChildren, infants: parsedInfants }));
    }
  }, [dispatch, navigate, startDate, endDate, adults, children, infants, current, hotelSettings]);

  const handleSearchTomorrowFromDeepLink = () => {
    if (!propcode || !startDate || !endDate) return;

    const nights = Math.max(1, dayjs(endDate).diff(dayjs(startDate), 'day'));
    const newCheckIn = dayjs().add(1, 'day').startOf('day');
    const newCheckOut = newCheckIn.add(nights, 'day');

    const params = new URLSearchParams({
      start_date: newCheckIn.format('YYYY-MM-DD'),
      end_date: newCheckOut.format('YYYY-MM-DD'),
      adults: String(Number(adults) || 2),
      children: String(Number(children) || 0),
      infants: String(Number(infants) || 0),
    });

    navigate(`/booking/${propcode}/availability?${params.toString()}`);
  };

  const handleQtyChange = useCallback((room: RoomData, rate: Rate, qty: number) => {
    dispatch(upsertQty({
      room,
      rate,
      qty,
    }));
  }, [dispatch]);

  const total = useMemo(() => cart.reduce((sum, i) => sum + i.subtotal, 0), [cart]);

  const handleGoGuest = () => {
    if (!propcode) return;

    const invalidFields: string[] = [];

    if (search.checkIn !== startDate) {
      invalidFields.push(
        intl.formatMessage({ id: 'booking.check_in', defaultMessage: 'Check-in' })
      );
    }

    if (search.checkOut !== endDate) {
      invalidFields.push(
        intl.formatMessage({ id: 'booking.check_out', defaultMessage: 'Check-out' })
      );
    }

    if (search.adults !== (Number(adults) || 2)) {
      invalidFields.push(
        intl.formatMessage({ id: 'booking.adults', defaultMessage: 'Adults' })
      );
    }
    if (search.children !== Number(children || 0)) {
      invalidFields.push(
        intl.formatMessage({ id: 'booking.children', defaultMessage: 'Children' })
      );
    }
    if (search.infants !== Number(infants || 0)) {
      invalidFields.push(
        intl.formatMessage({ id: 'booking.infants', defaultMessage: 'Infant' })
      );
    }

    if (invalidFields.length > 0) {
      message.error({
        content: intl.formatMessage(
          {
            id: 'booking.availability.invalidSearch',
            defaultMessage: '{fields} {count, plural, one {is invalid} other {are invalid}}. Please search rate and availability again.',
          },
          {
            fields: invalidFields.join(', '),
            count: invalidFields.length,
          }
        ),
        duration: 4,
      });
      return;
    }

    navigate(`/booking/${propcode}/guest`);
  };

  const renderRoomList = () => (
    <Space orientation="vertical" size={12} style={{ width: '100%' }}>
      {availableRooms.map((room: RoomData) => (
        <RoomCard
          key={room.roomtype_id}
          roomData={room} // แก้ชื่อ prop ให้ตรงกับ RoomCard Component (roomType)
          qtyMap={qtyMap}
          getCartKey={buildCartKey}
          onQtyChange={handleQtyChange}
          isMobile={isMobile}
        />
      ))}
    </Space>
  );

  // ฟังก์ชันสำหรับ Render รายการสรุป (ใช้ร่วมกันทั้ง Mobile และ Desktop เพื่อความสวยงามเหมือนกัน)
  // ฟังก์ชันสำหรับ Render รายการสรุป
  const renderCartItem = (it: CartItem) => (
    <List.Item key={it.cartKey} style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ width: '100%' }}>
        <Row wrap={false} gutter={16}>
          {/* ส่วนข้อมูล (ซ้าย) */}
          <Col flex="auto">
            <Typography.Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 4 }}>
              {it.room.roomtype_name}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: '14px', display: 'block', marginBottom: 2 }}>
              {it.rate.rateplan_name}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: '13px' }}>
              {it.nights} {intl.formatMessage({ id: 'booking.nights' })}
            </Typography.Text>
          </Col>

          {/* ส่วนราคาและปุ่ม (ขวา) */}
          <Col flex="none" style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            {/* ราคา */}
            <Typography.Text strong style={{ fontSize: '18px', color: token.colorPrimary, display: 'block', marginBottom: 12 }}>
              ฿ {it.subtotal.toLocaleString()}
            </Typography.Text>

            {/* ปุ่ม Control: ปรับกลับมาใช้แบบ Big & Clear */}
            <Space size={12} align="center">
              <Space.Compact style={{ verticalAlign: 'middle' }}>

                <InputNumber
                  mode='spinner'
                  min={0}
                  max={Math.max(0, it.room.room_avail - ((cartTotalsByRoomType[it.room.roomtype_id] || 0) - it.qty))}
                  value={it.qty}
                  style={{
                    width: '120px', // กำหนดความกว้างให้ตัวเลขชัดเจน
                    textAlign: 'center',
                    fontWeight: 'bold',
                    color: token.colorText // สีตัวอักษรปกติ
                  }}
                  onChange={(value) => dispatch(upsertQty({ room: it.room, rate: it.rate, qty: value as number }))}
                  size={isMobile ? 'middle' : 'large'}
                />

              </Space.Compact>

              {/* ปุ่มลบ (ถังขยะ) แยกออกมาให้กดง่ายๆ */}
              <Button
                type="text" // หรือใช้ type="default" danger ถ้าอยากให้มีกรอบแดงชัดๆ
                danger
                icon={<DeleteOutlined />}
                onClick={() => dispatch(upsertQty({ room: it.room, rate: it.rate, qty: 0 }))}
              />
            </Space>
          </Col>
        </Row>
      </div>
    </List.Item>
  );

  const googleHotelProfile: GoogleHotelProfile | null = propcode && hotelProfile?.hotelIntro?.title
    ? {
      hotelId: propcode,
      hotelName: hotelProfile.hotelIntro.title,
      currency: DEFAULT_CURRENCY,
      description: htmlToPlainText(hotelProfile.hotelIntro.content),
      images: uniq([hotelProfile.hotelBG, ...hotelProfile.hotelCarousel].filter(Boolean)),
      // แทนที่ closing block tag ด้วย "\n" ก่อน strip กัน text ของแต่ละ <p> ติดกัน (เช่น "...10310Thailand")
      address: htmlToPlainText(hotelProfile.address?.replace(/<\/(p|div|li|h[1-6])>/gi, '\n') ?? undefined) || null,
      facilities: hotelProfile.hotelIntro.facilities,
    }
    : null;

  const googleHotelSearch: GoogleHotelSearch = {
    startDate: startDate || '',
    endDate: endDate || '',
    adults: Number(adults) || 2,
    children: Number(children) || 0,
    infants: Number(infants) || 0,
  };

  const structuredData = startDate && endDate ? (
    <GoogleHotelMarkup hotel={googleHotelProfile} rooms={availableRooms} search={googleHotelSearch} />
  ) : null;

  const MainContent = (
    cutoffBlocked ? null :
    loading ? (
      <div style={{ minHeight: "40vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, fontSize: 18 }}>Loading...</div>
      </div>
    ) : error ? (
      <Alert title="Error" description={error} type="error" showIcon />
    ) : availableRooms.length === 0 ? <NoRoomsAvailable /> : renderRoomList()
  );

  const CutoffModal = (
    <BookingCutoffModal
      isOpen={cutoffBlocked}
      cutoffTime={hotelSettings?.booking_cutoff_time}
      onClose={() => setCutoffBlocked(false)}
      onSearchTomorrow={handleSearchTomorrowFromDeepLink}
    />
  );

  if (isMobile) {
    return (
      <>
        {structuredData}
        <div style={{ paddingBottom: '150px' }}>
          <BookingSearchCardVersion2 mode={"mobile"} propcode={propcode} />
          {MainContent}
        </div>
        <div
          className="bottom-summary"
          style={{
            background: token.colorBgElevated,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            boxShadow: (token as any).boxShadowTertiary || token.boxShadow,
            color: token.colorText,
          }}
        >
          <div className="bottom-summary__bar">
            <div className="bottom-summary__total" style={{ color: token.colorText }}>
              {intl.formatMessage({ id: 'booking.total' })}: ฿ {total.toLocaleString()}
            </div>
            <Button type="primary" size="large" disabled={cart.length === 0} onClick={handleGoGuest}>
              {intl.formatMessage({ id: 'booking.bookNow' })}
            </Button>
          </div>
          <div className="bottom-summary__collapse">
            <Collapse
              ghost
              items={[{
                key: 'cart',
                label: intl.formatMessage({ id: 'booking.selectedItemsDetails' }),
                children: (
                  cart.length === 0 ? <Typography.Paragraph type="secondary">{intl.formatMessage({ id: 'booking.noRoomSelected' })}</Typography.Paragraph> :
                    <List
                      dataSource={cart}
                      renderItem={renderCartItem} // ใช้ renderCartItem ที่สร้างใหม่
                    />
                )
              }]}
            />
          </div>
        </div>
        {CutoffModal}
      </>
    );
  }

  return (
    <>
    {structuredData}
    <Row gutter={[16, 16]}>
      <Col xs={24}>
        <BookingSearchCardVersion2 mode={"desktop"} propcode={propcode} />
      </Col>
      <Col xs={24} lg={16}>
        {MainContent}
      </Col>
      <Col xs={24} lg={8}>
        <div style={{ position: 'sticky', top: 16 }}>

          <Card title={intl.formatMessage({ id: 'booking.summary' })} style={{ borderRadius: 16 }}>
            {cart.length === 0 ? (
              <Typography.Paragraph type="secondary">{intl.formatMessage({ id: 'booking.noRoomSelected' })}</Typography.Paragraph>
            ) : (
              <List
                dataSource={cart}
                split={false} // ปิดเส้นคั่น default เพราะเราใส่เองใน renderCartItem แล้ว
                renderItem={renderCartItem} // ใช้ renderCartItem ที่สร้างใหม่
              />
            )}
            <Divider style={{ margin: '12px 0' }} />
            <Row justify="space-between" align="middle">
              <Typography.Title level={5} style={{ margin: 0 }}>{intl.formatMessage({ id: 'booking.total' })}</Typography.Title>
              <Typography.Title level={4} style={{ margin: 0, color: token.colorPrimary }}>฿ {total.toLocaleString()}</Typography.Title>
            </Row>

            <Button type="primary" size="large" block style={{ marginTop: 16 }} disabled={cart.length === 0} onClick={handleGoGuest}>
              {intl.formatMessage({ id: 'booking.bookNow' })}
            </Button>
            <Button type="default" size="large" block style={{ marginTop: 12 }} onClick={() => navigate(`/booking/${propcode}`)}>{intl.formatMessage({ id: 'label.back' })}</Button>
          </Card>
        </div>
      </Col>
    </Row>
    {CutoffModal}
    </>
  );
}

export default Availability
